import { useMemo } from 'react';
import classNames from 'classnames';
import { FormProvider, type Resolver, useForm } from 'react-hook-form';
import type { ObjectSchema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { defaultValues, formSchema, type BuscadorNotariosFormValues } from './config';
import CTNButton from '@ctn/ui/components/ctn-button';
import CTNInput from '@ctn/ui/components/ctn-input';

type BuscadorNotariosFormProps = {
	readonly className?: string;
	readonly onSubmitSuccess?: (payload: BuscadorNotariosFormValues) => void;
};

const classNameComponent = 'c-cnm-buscadores__form';

export const BuscadorNotariosForm = ({ className, onSubmitSuccess }: BuscadorNotariosFormProps) => {
	const wrapperClassName = classNames(classNameComponent, className);

	const resolver = useMemo<Resolver<BuscadorNotariosFormValues>>(
		() =>
			yupResolver<BuscadorNotariosFormValues>(formSchema as unknown as ObjectSchema<BuscadorNotariosFormValues>),
		[],
	);

	const methods = useForm<BuscadorNotariosFormValues>({
		mode: 'onTouched',
		resolver,
		defaultValues,
	});

	const {
		control,
		formState: { errors, isSubmitting },
		handleSubmit,
	} = methods;

	const submit = handleSubmit((data: BuscadorNotariosFormValues) => {
		const payload: BuscadorNotariosFormValues = {
			...data,
		};
		onSubmitSuccess?.(payload);
	});

	return (
		<div className={wrapperClassName}>
			<h3 className={`${classNameComponent}-title`}>Buscador de notarios</h3>
			<FormProvider {...methods}>
				<form noValidate className="[ o-grid ] [ u-mt-16 ]" onSubmit={(e) => e.preventDefault()}>
					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="name"
								label="Nombre"
								classNameField={`${classNameComponent}-field`}
								classNameInput={`${classNameComponent}-input`}
								error={errors.name?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="surname"
								label="Apellidos"
								classNameField={`${classNameComponent}-field`}
								classNameInput={`${classNameComponent}-input`}
								error={errors.surname?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="domicilio"
								label="Domicilio"
								classNameField={`${classNameComponent}-field`}
								classNameInput={`${classNameComponent}-input`}
								error={errors.domicilio?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="poblacion"
								label="Poblacion"
								classNameField={`${classNameComponent}-field`}
								classNameInput={`${classNameComponent}-input`}
								error={errors.poblacion?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12">
							<CTNInput
								type="text"
								control={control}
								name="cp"
								label="Código Postal"
								classNameField={`${classNameComponent}-field`}
								classNameInput={`${classNameComponent}-input`}
								error={errors.cp?.message}
							/>
						</div>
					</div>

					<div className="o-grid__row">
						<div className="o-grid__col-12 o-grid__col-6@landscape o-grid__col-3@desktop">
							<CTNButton
								type="button"
								variant="solid"
								mode="primary"
								className={`${classNameComponent}-btn`}
								label="Buscar"
								onClick={submit}
							/>
						</div>
					</div>
				</form>
			</FormProvider>
		</div>
	);
};
